import { useEffect, useState } from "react";
import api from "../../lib/api";
import {
  Users, Workflow, Wrench, AlertTriangle, Sparkles, TrendingUp, Clock, Box,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import ScSection from "./dashboard/ScSection";
import OpportunitySection from "./dashboard/OpportunitySection";
import QuotationSection from "./dashboard/QuotationSection";
import TicketSection from "./dashboard/TicketSection";
import CustomerReferralSection from "./dashboard/CustomerReferralSection";
import CustomerAppSection from "./dashboard/CustomerAppSection";
import InstalledBaseSection from "./dashboard/InstalledBaseSection";

function KPI({ label, value, sub, icon: Icon, accent, to }) {
  const navigate = useNavigate();
  return (
    <div
      onClick={to ? () => navigate(to) : undefined}
      className={"border border-[#E2E4DC] bg-white p-5 hover:border-[#0A1128] transition-all duration-150" + (to ? " cursor-pointer hover:shadow-[0_6px_18px_rgba(15,23,42,0.08)] hover:-translate-y-0.5" : "")}
      data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      <div className="flex items-center justify-between">
        <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#4A5568]">{label}</div>
        <Icon className="w-4 h-4 text-[#4A5568]" />
      </div>
      <div className={"font-display font-bold mt-3 leading-none " + (accent ? "text-[#1F4E2C] text-5xl" : "text-[#0A1128] text-4xl")}>{value}</div>
      {sub && <div className="text-xs text-[#4A5568] mt-2">{sub}</div>}
    </div>
  );
}

const CAT_COLORS = {
  hot: "bg-red-500",
  warm: "bg-amber-500",
  cold: "bg-sky-500",
};

export default function Dashboard() {
  const [s, setS] = useState(null);
  const [lowStock, setLowStock] = useState([]);
  const [scs, setScs] = useState([]);
  const [leads, setLeads] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [extra, setExtra] = useState(null);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setS(r.data));
    api.get("/inventory/low-stock").then((r) => setLowStock(r.data));
    api.get("/admin/sc").then((r) => setScs(r.data));
    api.get("/leads").then((r) => setLeads(r.data));
    api.get("/opportunities").then((r) => setOpportunities(r.data));
    api.get("/quotations").then((r) => setQuotations(r.data));
    api.get("/service-tickets").then((r) => setTickets(r.data));
    api.get("/referrer-payouts").then((r) => setPayouts(r.data));
    api.get("/dashboard/insights-extra").then((r) => setExtra(r.data));
  }, []);

  if (!s) return <div className="text-sm font-mono text-[#4A5568]">Loading…</div>;

  const totalCats = Object.values(s.leads_by_category).reduce((a, b) => a + b, 0) || 1;

  return (
    <div className="space-y-6" data-testid="crm-dashboard">
      <div>
        <div className="font-mono text-xs tracking-[0.3em] uppercase text-[#4A5568]">Overview / Today</div>
        <h1 className="font-display text-5xl lg:text-6xl font-bold tracking-tighter leading-none mt-3">
          The grid is <span className="italic font-light text-[#1F4E2C]">live.</span>
        </h1>
        <p className="text-sm text-[#4A5568] mt-3 max-w-2xl">
          Snapshot of the entire solar pipeline — from rooftop enquiry to subsidy disbursement.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border border-[#E2E4DC] divide-x divide-y lg:divide-y-0 divide-[#E2E4DC] bg-white">
        <KPI label="Active Leads" value={s.leads_open} sub={`${s.leads_total} total`} icon={Sparkles} accent to="/crm/leads" />
        <KPI label="Open Opportunities" value={s.opportunities_active} icon={Workflow} to="/crm/opportunities" />
        <KPI label="Open Tickets" value={s.tickets_open} sub={`${s.sla_avg_hours}h SLA avg`} icon={Wrench} to="/crm/tickets" />
        <KPI label="Low-Stock Items" value={s.low_stock_count} sub={`of ${s.materials_total} SKUs`} icon={AlertTriangle} to="/crm/inventory" />
      </div>

      {/* ───────────────────────── Executive Insights ───────────────────────── */}
      <div className="pt-2">
        <div className="font-mono text-xs tracking-[0.3em] uppercase text-[#4A5568]">Executive Insights</div>
        <p className="text-sm text-[#4A5568] mt-2 max-w-2xl">
          Click any KPI or chart to drill straight into the full report. Every section tells a different story — SC network,
          opportunity pipeline, quotations, tickets, installed base and the Customer App.
        </p>
      </div>

      <div className="space-y-4">
        <ScSection scs={scs} leads={leads} opportunities={opportunities} payouts={payouts} delay={0} />
        <OpportunitySection opportunities={opportunities} delay={60} />
        <QuotationSection quotations={quotations} delay={120} />
        <TicketSection tickets={tickets} delay={180} />
        <InstalledBaseSection extra={extra} delay={240} />
        <CustomerAppSection extra={extra} delay={300} />
        <CustomerReferralSection leads={leads} payouts={payouts} delay={360} />
      </div>

      {/* ───────────────────────── Operational Detail ───────────────────────── */}
      <div className="pt-4 border-t border-[#E2E4DC]">
        <div className="font-mono text-xs tracking-[0.3em] uppercase text-[#4A5568]">Operational Detail</div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Lead Pipeline */}
        <div className="lg:col-span-2 border border-[#E2E4DC] bg-white p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#4A5568]">Lead Pipeline</div>
              <div className="font-display text-2xl font-bold mt-1">By stage</div>
            </div>
            <Link to="/crm/leads" className="font-mono text-xs uppercase tracking-widest text-[#1F4E2C] hover:underline">
              View →
            </Link>
          </div>
          <div className="space-y-3">
            {["open", "accepted", "on_hold", "dropped", "converted"].map((st) => {
              const count = s.leads_pipeline[st] || 0;
              const pct = (count / (s.leads_total || 1)) * 100;
              return (
                <Link key={st} to="/crm/leads" className="flex items-center gap-4 group">
                  <div className="w-32 font-mono text-xs uppercase tracking-widest text-[#4A5568]">{st.replace("_", " ")}</div>
                  <div className="flex-1 h-7 bg-[#F4F5F0] relative overflow-hidden group-hover:bg-[#EEF0E8] transition-colors">
                    <div
                      className={"absolute inset-y-0 left-0 transition-all " + (st === "converted" ? "bg-[#0A1128]" : st === "dropped" ? "bg-[#94A3B8]" : "bg-[#1F4E2C]")}
                      style={{ width: `${pct}%` }}
                    />
                    <div className="absolute inset-0 flex items-center px-3 font-mono text-xs text-[#0A1128] mix-blend-difference text-white">
                      {count}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Lead temperature */}
        <div className="border border-[#E2E4DC] bg-white p-6">
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#4A5568]">Lead Temperature</div>
          <div className="font-display text-2xl font-bold mt-1 mb-4">Heat map</div>
          <div className="flex h-40 items-end gap-3">
            {Object.entries(s.leads_by_category).map(([cat, n]) => (
              <Link key={cat} to="/crm/leads" className="flex-1 flex flex-col items-center gap-2 group">
                <div className="font-mono text-xs">{n}</div>
                <div className={"w-full transition-opacity group-hover:opacity-80 " + CAT_COLORS[cat]} style={{ height: `${(n / totalCats) * 100}%` }} />
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#4A5568]">{cat}</div>
              </Link>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3 mt-6 pt-6 border-t border-[#E2E4DC]">
            <div>
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#4A5568]">Avg SLA</div>
              <div className="font-display text-2xl font-bold mt-1 flex items-center gap-2">
                {s.sla_avg_hours}<span className="text-sm font-mono text-[#4A5568]">hrs</span>
              </div>
            </div>
            <div>
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#4A5568]">Breach</div>
              <div className="font-display text-2xl font-bold mt-1 text-red-600">{s.sla_breach_pct}%</div>
            </div>
          </div>
        </div>
      </div>

      {/* Low stock */}
      <div className="border border-[#E2E4DC] bg-white">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E2E4DC]">
          <div>
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#4A5568]">Inventory</div>
            <div className="font-display text-2xl font-bold mt-1">Low-stock alerts</div>
          </div>
          <Link to="/crm/inventory" className="font-mono text-xs uppercase tracking-widest text-[#1F4E2C] hover:underline">
            View all →
          </Link>
        </div>
        {lowStock.length === 0 ? (
          <div className="p-8 text-sm text-[#4A5568] font-mono">No materials below threshold. ⚡</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left font-mono text-[10px] uppercase tracking-[0.25em] text-[#4A5568] border-b border-[#E2E4DC]">
                <th className="px-6 py-3">Code</th>
                <th className="px-6 py-3">Name</th>
                <th className="px-6 py-3 text-right">Qty</th>
                <th className="px-6 py-3 text-right">Min</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {lowStock.map((m) => (
                <tr key={m.id} className="border-b border-[#E2E4DC] hover:bg-[#F4F5F0]" data-testid={`low-stock-row-${m.code}`}>
                  <td className="px-6 py-3 font-mono">{m.code}</td>
                  <td className="px-6 py-3">{m.name}</td>
                  <td className="px-6 py-3 text-right font-mono text-red-600">{m.quantity}</td>
                  <td className="px-6 py-3 text-right font-mono">{m.min_quantity}</td>
                  <td className="px-6 py-3 text-right">
                    <span className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-red-600 bg-red-50 px-2 py-1">
                      <AlertTriangle className="w-3 h-3" /> Re-order
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
